import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  AssignParcelClerksDto,
  PlaceInListDto,
  RouteInListDto,
  RouteManagementApi,
  TenantUsersApi,
  TenantUsersDto,
} from '@buspro/web/apis';
import { ErrorDisplayComponent, ProblemDetail, useModalErrorHandler } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-update-assigned-parcel-clerks-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    NzModalModule,
    NzSelectModule,
    ReactiveFormsModule,
    FormsModule,
    NzListModule,
    NzButtonComponent,
    ErrorDisplayComponent,
  ],
  template: `
    <nz-modal
      (nzOnCancel)="onHide()"
      [nzClosable]="true"
      [nzVisible]="show"
      [nzWidth]="400"
      [nzFooter]="null"
    >
      <div *nzModalTitle class="font-semibold">
        Update Assigned Parcel Clerks
      </div>
      <div *nzModalContent>
        <app-error-display *ngIf="showError() && errorDetails" [error]="errorDetails" (dismiss)="onDismissError()"></app-error-display>
        <nz-form-item>
          <nz-form-label [nzRequired]="true" nzSpan="24"
            >Select Clerks for {{ route.name }} route
          </nz-form-label>
          <nz-form-control nzSpan="24" class="flex flex-row nowrap">
            <nz-select
              nzShowSearch="true"
              [nzPlaceHolder]="'Choose...'"
              [nzNotFoundContent]="'No parcel clerks available'"
              [nzDisabled]="parcelClerksQuery.isLoading()"
              [nzLoading]="parcelClerksQuery.isLoading()"
              [(ngModel)]="selectedParcelClerkId"
            >
              <nz-option
                *ngFor="let su of availableParcelClerks"
                [nzLabel]="su.name"
                [nzValue]="su.id"
              >
              </nz-option>
            </nz-select>
            <button nz-button nzType="primary" (click)="addClerk()">
              Assign
            </button>
          </nz-form-control>
        </nz-form-item>

        <h3>Assigned Users</h3>
        <ul
          nz-list
          [nzNoResult]="'No clerks assigned to this route'"
          [nzLoading]="currentlyAssignedUsersQuery.isLoading()"
          [nzDataSource]="currentlyAssignedUsers"
          [nzBordered]="true"
          nzSize="default"
        >
          <li *ngFor="let item of currentlyAssignedUsers" nz-list-item nzNoFlex>
            <ul nz-list-item-actions>
              <nz-list-item-action>
                <a (click)="removeClerk(item.id)">Remove</a>
              </nz-list-item-action>
            </ul>
            {{ item.name }}
          </li>
        </ul>
      </div>
    </nz-modal>
  `,
  styles: ``,
})
export class UpdatedAssignedParcelClerksModalComponent implements OnInit {
  get route(): RouteInListDto {
    return this._route;
  }

  @Input()
  set route(value: RouteInListDto) {
    this._route = value;
    const clerkIds = value.parcelClerksAssignments.map((bc) => bc.userId);
    this.listOfSelectedParcelClerksIds.set(clerkIds);
  }
  get show(): boolean {
    return this._show;
  }
  @Input()
  set show(value: boolean) {
    this._show = value;
    this.parcelClerksQuery.refetch();
  }
  private formBuilder = inject(FormBuilder);
  private usersApi = inject(TenantUsersApi);
  private routesApi = inject(RouteManagementApi);

  private _show = false;

  @Input()
  terminal!: PlaceInListDto;

  private _route!: RouteInListDto;

  @Output()
  hide = new EventEmitter(true);

  parcelClerksQuery = injectQuery(() => ({
    queryKey: ['parcel-clerks-eligible-for-route', this.route?.id ?? ''],
    queryFn: () =>
      lastValueFrom(
        this.usersApi.tenantUsersControllerGetParcelClerksEligibleForRoute({
          routeId: this.route.id,
        }),
      ),
    enabled: !!this.route?.id,
  }));

  currentlyAssignedUsersQuery = injectQuery(() => ({
    queryKey: ['route-clerks', this.listOfSelectedParcelClerksIds()],
    queryFn: () =>
      lastValueFrom(
        this.usersApi.tenantUsersControllerGetSpecificList({
          body: {
            userIds: this.listOfSelectedParcelClerksIds(),
          },
        }),
      ),
  }));

  get currentlyAssignedUsers(): TenantUsersDto[] {
    return this.currentlyAssignedUsersQuery.data() ?? [];
  }

  listOfSelectedParcelClerksIds = signal<string[]>([]);

  get parcelClerks(): TenantUsersDto[] {
    return this.parcelClerksQuery.data() ?? [];
  }

  private modalErrorHandler = useModalErrorHandler();
  showError = signal(false);
  errorDetails: ProblemDetail | null = null;

  constructor(private notification: NzNotificationService) {}

  ngOnInit(): void {
    this.parcelClerksQuery.refetch();
  }

  onHide() {
    this.hide.emit(true);
    this._show = false;
  }

  selectedParcelClerkId: string | null = null;

  get availableParcelClerks() {
    const assignedClerkIds = this.currentlyAssignedUsers.map((user) => user.id);
    return this.parcelClerks.filter((clerk) => !assignedClerkIds.includes(clerk.id));
  }

  addClerk() {
    if (this.selectedParcelClerkId) {
      const selectedClerk = this.parcelClerks.find((clerk) => clerk.id === this.selectedParcelClerkId);
      if (selectedClerk) {
        this.listOfSelectedParcelClerksIds.update((ids) => [...ids, selectedClerk.id]);
        this.selectedParcelClerkId = null;
        this.updateAssignedClerks();
      }
    }
  }

  removeClerk(clerkId: string) {
    this.listOfSelectedParcelClerksIds.update((ids) => ids.filter((id) => id !== clerkId));
    this.updateAssignedClerks();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  updateAssignedClerks() {
    const payload: AssignParcelClerksDto = {
      parcelClerksIds: this.listOfSelectedParcelClerksIds(),
    };

    this.routesApi.routeControllerAssignParcelClerks({ id: this._route.id, body: payload }).subscribe(
      () => {
        this.clearErrors();
        this.notification.success(`Booking clerks updated for ${this._route.name}`, '');
        this.parcelClerksQuery.refetch();
      },
      (error) => {
        const result = this.modalErrorHandler.handleModalError(error, {
          modalName: 'update-assigned-parcel-clerks',
          action: 'update-assigned-parcel-clerks',
        });
        this.errorDetails = result.problemDetail ?? null;
        this.showError.set(!!this.errorDetails);
        if (!result.shouldHandleGlobally) {
          this.notification.error('Update Failed', result.userMessage);
        }
      },
    );
  }
}
